// WebAuthn (FIDO2) browser glue for CMK step-up.
//
// The server (cmk_routes.webauthn_stepup, py_webauthn) speaks the standard
// WebAuthn JSON: challenge / user.id / credential ids arrive as base64url
// strings and must be handed to navigator.credentials.* as ArrayBuffers, and
// the authenticator's binary response must go back as base64url. This module is
// the only place that conversion lives.
//
// enrolAuthenticator() runs the registration ceremony (register a security key
// / passkey). stepUp() runs the authentication ceremony and returns the
// short-lived token that gates a protected operation (e.g. a tier change).

import {
  webauthnRegisterBegin, webauthnRegisterComplete,
  webauthnAuthBegin, webauthnAuthComplete,
} from './api'

/** True when this browser + context can do WebAuthn (needs a secure context). */
export function isWebAuthnAvailable(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && !!navigator.credentials
}

function b64uToBuf(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4)
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

function bufToB64u(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The step-up begin call returns this friendly message when the tenant has no
 * enrolled authenticator yet — the UI turns it into an "enrol first" prompt. */
export const NO_AUTHENTICATOR = 'no-authenticator'

/** Register a security key / passkey for the current organisation. */
export async function enrolAuthenticator(): Promise<{ credential_id?: string }> {
  const opts = await webauthnRegisterBegin()
  const challengeId: string = opts.challenge_id
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    user: { ...opts.user, id: b64uToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c: { id: string }) => ({
      ...c, id: b64uToBuf(c.id),
    })),
  }
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('Enrolment was cancelled.')
  const resp = cred.response as AuthenticatorAttestationResponse
  const credential = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(resp.clientDataJSON),
      attestationObject: bufToB64u(resp.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  }
  return webauthnRegisterComplete(credential, challengeId)
}

/** Run the authentication ceremony for `operation` and return the step-up token.
 * Throws an Error whose message is NO_AUTHENTICATOR when nothing is enrolled. */
export async function stepUp(operation: string): Promise<string> {
  let opts: {
    challenge: string; challenge_id: string; rpId?: string
    allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>
    [k: string]: unknown
  }
  try {
    opts = await webauthnAuthBegin(operation)
  } catch (err: unknown) {
    // auth/begin 400s with "No registered authenticator — enrol one first".
    const msg = extractError(err)
    if (/no registered authenticator|enrol one first/i.test(msg)) {
      throw new Error(NO_AUTHENTICATOR)
    }
    throw new Error(msg)
  }
  const challengeId = opts.challenge_id
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      id: b64uToBuf(c.id),
      type: 'public-key' as const,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  }
  const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
  if (!assertion) throw new Error('Verification was cancelled.')
  const resp = assertion.response as AuthenticatorAssertionResponse
  const credential = {
    id: assertion.id,
    rawId: bufToB64u(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufToB64u(resp.clientDataJSON),
      authenticatorData: bufToB64u(resp.authenticatorData),
      signature: bufToB64u(resp.signature),
      ...(resp.userHandle ? { userHandle: bufToB64u(resp.userHandle) } : {}),
    },
    clientExtensionResults: assertion.getClientExtensionResults(),
  }
  const { webauthn_token } = await webauthnAuthComplete(credential, challengeId)
  return webauthn_token
}

function extractError(err: unknown): string {
  if (err && typeof err === 'object') {
    const anyErr = err as { response?: { data?: { error?: string } }; message?: string }
    return anyErr.response?.data?.error || anyErr.message || 'WebAuthn request failed'
  }
  return 'WebAuthn request failed'
}
